/**
 * Thin HTTP client for /api/orgs/connections (step 9a).
 *
 * Mirrors @papan/orgs/client style. Server contract is in
 * features/org-connections/api/routes.ts; types here are the
 * transport shape, not the DB shape.
 */
import { request } from '@papan/api-client'

/** Build identity-override headers (x-tenant-id / x-org-email) so a call can act
 *  as an owner/admin of ANOTHER tenant than the active one — the cross-tenant
 *  "approve/confirm from a notification without switching" path. Omitted keys
 *  fall back to the active identity the api-client getters supply. */
function asHeaders(as?: { tenantId?: string | null; orgEmail?: string | null }): Record<string, string> {
  const h: Record<string, string> = {}
  if (as?.tenantId) h['x-tenant-id'] = as.tenantId
  if (as?.orgEmail) h['x-org-email'] = as.orgEmail
  return h
}

export interface ConnectionRow {
  connectionId: string
  org: {
    tenantId: string
    name: string
    type: string
    memberCount: number
  }
  /** Stable human-facing identity for the card: a person's display name +
   *  email (preferred over the raw personal-tenant name), or the org name. */
  peerIdentity?: { kind: 'personal' | 'organization'; displayName: string; email?: string }
  establishedAt: string
  /** Audit emails are FIXED request roles (not perspective-relative):
   *  the same connection reads identically in both tenants' views. */
  /** "Initiated by" — who SENT the connection request. */
  initiatorEmail: string
  /** "Accepted by" — who ACCEPTED the request. */
  accepterEmail: string
  /** Org name the initiator connected as (shown after the email). */
  initiatorOrgName: string
  /** Org name the accepter connected as (shown after the email). */
  accepterOrgName: string
  /** "Referred by" — the org that PROPOSED this connection via a reshare
   *  referral (distinct from the content-owner initiator). Absent otherwise. */
  referredByEmail?: string
  referredByOrgName?: string | null
  /** This tenant's own default contact for the connection (NULL ⇒ UI
   *  falls back to the audit connected_by email shown by the server). */
  myDefaultContact: string | null
  /** The other side's default contact (already falls back to its own
   *  audit email server-side if unset). For Share-dialog prefill. */
  theirDefaultContact: string
  /** The EXPLICIT peer-contact override (null ⇒ none set; `theirDefaultContact`
   *  is then the audit fallback). Lets the connection card edit the override
   *  without pinning the fallback as an explicit value on a no-op save. */
  theirDefaultContactExplicit: string | null
  /** Set when the OTHER side is a personal tenant whose owner's primary
   *  email no longer matches the audit email captured at connection
   *  time. Null otherwise. The UI uses this to surface a "peer
   *  renamed — re-confirm?" affordance; access continues to work
   *  unchanged (tenant id is still the resolver's identity). */
  peerRenamed: { auditEmail: string; currentEmail: string | null } | null
}

export interface InitiateArgs {
  /** Optional — when set, MUST be an already-active connection of the
   *  caller (the picker is connection-scoped per the privacy invariant). */
  recipientCompanyTenantId?: string | null
  recipientEmail: string
  /** null/omitted = connection-only initiate. The recipient's accept
   *  creates only the org_connections row — no grant, no
   *  _shared/<SourceOrg>/ tree, no canonical .shared. Use a real folder
   *  id (e.g. from the Share dialog) to share specific content. */
  sourceFolderId?: string | null
  /** Extra folders shared ALONGSIDE the primary in this ONE request (the chain
   *  targets contained in `sourceFolderId`) — so the recipient accepts once and
   *  receives them all. */
  additionalFolderIds?: string[]
  permission?: 'read' | 'readcopy' | 'readwrite' | 'admin'
  note?: string | null
  /** Pin the initiator's default contact on the connection when it
   *  establishes (composer "set as default contact" toggle). */
  setAsDefaultContact?: boolean
  /** Share dialog "Allow sharing" — whether the recipient may re-share.
   *  Recorded on the grant and enforced by Gate A on onward re-share. */
  allowSharing?: boolean | null
  /** Org name the initiator typed — shown in the recipient's request
   *  popup (display-only, rides the notification). */
  organizationName?: string | null
  /** Recipient was chosen as a PERSON (not an org). Restricts the
   *  already-connected / silent-reshare resolution to their personal
   *  tenant, so you can share with them individually even if they belong
   *  to an org you're already connected to. */
  recipientIsPersonal?: boolean
  /** A LEVEL CHANGE of an existing share to the connected `recipientCompanyTenantId`: the
   *  current direct grants on `sourceFolderId` are replaced in one step — refused whole
   *  (409 LEVEL_CHANGE_UNDELIVERABLE, or a 429 from the card's rate limit), the old access stands. */
  replacesAccess?: boolean
}

export interface InitiateResponse {
  ok: true
  /** `pending_recipient` = standard handshake (a request row was
   *  written; recipient picks Personal/Org, initiator confirms).
   *  `established` = silent re-share fast-path: sender and recipient
   *  were already connected, so the grant + canonical .shared
   *  materialized in one shot (no share_requests row, no recipient
   *  picker, no notification). Doc § "Reverse-share & transitive
   *  re-share": "Connection bidirectional ⇒ already connected: no
   *  handshake, just the per-share dialog." */
  state: 'pending_recipient' | 'established' | 'pending_source_approval'
  /** Reshare referral: the content was inbound cross-tenant, so instead of a
   *  double-mount the server created a request the ORIGINAL owner must approve.
   *  The UI shows "sent — waiting for the owner to approve" rather than
   *  "Shared". The recipient isn't notified until the owner approves. */
  reshareReferral?: boolean
  /** Server flag: a reshare to an unconnected recipient became a CONNECT request
   *  (the referral fires once it's accepted). UI shows "connection request sent",
   *  not "Shared". */
  deferredReshareConnect?: boolean
  /** Always present, always a UUID. On silent-block this is a fabricated
   *  id (no DB row) — the response shape is identical to a real initiate
   *  so the sender cannot probe to detect blocking. On silent re-share
   *  it is also fabricated (no row exists for it). */
  requestId: string
  /** Server flag: true iff this was a silent re-share (already-connected
   *  fast-path). UI uses this to render a "Shared with X" toast instead
   *  of "Share request sent". Absent (or false) on the handshake path. */
  silentReshare?: boolean
  /** The recipient-tree id of the canonical `.shared` row that was
   *  materialized (or reused on dedup). Only set when
   *  `silentReshare === true`. */
  canonicalSharedId?: string | null
}

export interface FolderAccessRow {
  /** Recipient tenant id (the "outsider" — a tenant other than the
   *  caller's that holds a share_grant rooted at or above the folder). */
  tenantId: string
  name: string
  type: string
  /** Anonymous count per the privacy view — never enumerates members. */
  memberCount: number
  /** Distinct grants rooted at/above this folder for this recipient.
   *  Signal of how broadly the recipient has access (one grant on the
   *  folder vs. many grants on subtree ancestors). */
  shareCount: number
  /** True iff at least one grant is directly on this folder (vs.
   *  inherited from an ancestor). UI enables Remove only when true. */
  directGrant: boolean
  /** True iff a `hidden` grant sits directly on this folder for the recipient
   *  (the cross-tenant "No access" tier). Drives bucketing into No-access + the
   *  un-hide reverse drop — independent of maxPermission. */
  directHidden: boolean
  /** True iff the recipient also holds a grant on a STRICT ANCESTOR of this
   *  folder (inherited access). false ⇒ this folder is the recipient's access
   *  root, so "No access" REVOKES; true ⇒ "No access" sets a reversible hidden. */
  hasInheritedAccess: boolean
  /** MAX permission across all matching grants (read | readcopy |
   *  readwrite | admin). Kept for legacy callers; the UI buckets by
   *  nearestPermission. */
  maxPermission: string
  /** NEAREST-explicit-grant permission (the grant at the smallest depth —
   *  this folder if it has its own grant, else the closest ancestor; 'hidden'
   *  wins same-depth ties). Mirrors the resolver, so a child raised/lowered
   *  above/below its parent buckets into its own tier. */
  nearestPermission: string
  /** The direct grant's re-sharing flag (Gate A). true = may re-share;
   *  false = "No sharing". (NULL/unset is reported as true.) */
  allowSharing: boolean
  firstSharedAt: string
  /** Provenance: when set, this recipient's access arrived via a reshare
   *  referral THIS org (the owner) approved — the named org referred them.
   *  The owner's view annotates "reshared via <org>". null for a direct share. */
  reshareVia?: string | null
  /** Resharer marker: this row is a reshare YOU made of inbound content — the
   *  source owner holds the actual grant, so it's non-removable here. Shown as
   *  "reshared by you · <state>". */
  reshareByYou?: boolean
  reshareState?: 'pending' | 'approved'
  /** The referral's request id (reshareByYou rows only) — lets the resharer
   *  edit the proposed access / cancel it while it's still pending the owner. */
  requestId?: string
  recipientEmail?: string
}

/** A connection awaiting the caller's confirmation — surfaced in the
 *  avatar menu (cross-tenant, owner/admin only). */
export interface PendingConfirmation {
  requestId: string
  /** The caller's tenant that initiated — they must confirm AS this. */
  initiatorTenantId: string
  initiatorTenantName: string
  /** The recipient that accepted to join (who they'd be connecting to). */
  recipientEmail: string
  sourceFolderId: string | null
  grantedPermission: string
  /** #848 — from a share link: the most the link gives the person who asked (the
   *  approval dialog's cap). Null/absent = not from a link. */
  linkCap?: string | null
  stateChangedAt: string
}

/** A reshare referral awaiting THIS org's (the source owner's) approval. */
export interface ReshareReferral {
  requestId: string
  /** The owner's own folder being reshared (resolved in the owner's scope). */
  sourceFolderName: string | null
  sourceIsFolder: boolean
  /** Who the resharer wants it shared with. */
  recipientEmail: string
  /** The recipient's PERSON name (their user name, or the email local-part when
   *  they have none) — distinct from `recipientOrgName` below, which is the
   *  identity they receive AS. Lets the approval popup name them rather than
   *  showing a bare address. */
  recipientName: string | null
  /** The recipient's CHOSEN receiving identity (org name, or "Personal") — set
   *  because Option 2 has the recipient pick before the owner approves. Null on a
   *  referral whose recipient tenant hasn't resolved. */
  recipientOrgName: string | null
  /** The resharer's org + email (who proposed it). */
  referrerOrgName: string | null
  referrerEmail: string | null
  permission: string
  /** #848 — an Add-to-Drive referral (from a share link): its approval can't go past the
   *  link's View. */
  fromShareLink?: boolean
  createdAt: string
}

export interface IncomingRequest {
  requestId: string
  initiatorTenantId: string
  initiatorEmail: string
  /** null = connection-only request (no specific folder being shared yet). */
  sourceFolderId: string | null
  grantedPermission: string
  initiatorNote: string | null
  createdAt: string
  expiresAt: string
}

export interface OutgoingRequest {
  requestId: string
  state:
    | 'draft'
    | 'pending_recipient'
    | 'pending_initiator_confirm'
    | 'closed_rejected'
  /** Set when the request was auto-completed because the two orgs got
   *  connected by a sibling request. The outbox shows it as resolved
   *  ("Completed · already connected") instead of vanishing. */
  /** `recipient_deleted` (#1213): closed because the organization or account it was addressed to was deleted. */
  completed: 'already_connected' | 'recipient_deleted' | null
  /** "Initiated by" — the org member who sent the request (the outbox is
   *  tenant-wide, so this may be a colleague, not the viewer). */
  initiatorEmail: string
  recipientEmail: string
  recipientTenantId: string | null
  /** null = connection-only request. */
  sourceFolderId: string | null
  /** Every item the request shares, as the drive names them: its primary (the
   *  compound id for a deferred sub-item reshare, else `sourceFolderId`) and the
   *  folders bundled with it. Absent from an older API. */
  sharedItemIds?: string[]
  grantedPermission: string
  /** The stored re-sharing choice (null = allowed). Absent from an older API. */
  allowSharing?: boolean | null
  /** May the caller withdraw it — the same gate the action asks. Absent from an older API. */
  canCancel?: boolean
  initiatorNote: string | null
  stateChangedAt: string
  expiresAt: string
}

export const orgConnectionsApi = {
  async list() {
    return request<{ connections: ConnectionRow[] }>('/api/orgs/connections')
  },

  /** 9b: which external tenants have access to `folderId` (or any
   *  ancestor) via a share_grant. Privacy view per the doc. */
  async listFolderAccess(folderId: string) {
    return request<{ folderAccess: FolderAccessRow[] }>(
      `/api/orgs/connections/folder-access/${encodeURIComponent(folderId)}`,
    )
  },

  /** Share helper: external same-tenant `.chain` targets referenced from INSIDE
   *  `folderId` (chains pointing outside the folder). Sharing the folder alone
   *  leaves those chains broken for the recipient — the Share dialog offers to
   *  share these targets too. Backed by the files API (org-connections can't
   *  import @papan/files — that would cycle). Empty / fails-soft to []. */
  async getChainRefs(folderId: string): Promise<Array<{ id: string; name: string; isFolder: boolean }>> {
    return request<{ refs: Array<{ id: string; name: string; isFolder: boolean }> }>(
      `/api/files/chain-refs?folderId=${encodeURIComponent(folderId)}`,
    ).then(r => r.refs).catch(() => [])
  },

  /** Revoke direct grants for (this folder, recipient tenant). Inherited grants
   *  on ancestors are untouched. `onlyHidden` revokes JUST the `hidden` grant
   *  (un-hide → revert to inherited); otherwise revokes ALL direct grants (the
   *  share-root "remove access"). Caller must have admin on the folder. */
  async revokeFolderAccess(folderId: string, tenantId: string, opts?: { onlyHidden?: boolean }) {
    const qs = opts?.onlyHidden ? '?permission=hidden' : ''
    return request<{ ok: true; revoked: number }>(
      `/api/orgs/connections/folder-access/${encodeURIComponent(folderId)}/${encodeURIComponent(tenantId)}${qs}`,
      { method: 'DELETE' },
    )
  },

  /** Edit a direct outsider grant in place — change its permission level
   *  (drag-to-tab) and/or its re-sharing flag. Admin on the folder required. */
  async updateFolderAccess(folderId: string, tenantId: string, patch: { permission?: 'read' | 'readcopy' | 'readwrite' | 'hidden'; allowSharing?: boolean }) {
    return request<{ ok: true; updated: number }>(
      `/api/orgs/connections/folder-access/${encodeURIComponent(folderId)}/${encodeURIComponent(tenantId)}`,
      { method: 'PATCH', body: JSON.stringify(patch), headers: { 'Content-Type': 'application/json' } },
    )
  },

  async setDefaultContact(connectionId: string, email: string | null) {
    return request<{ ok: true }>(
      `/api/orgs/connections/${connectionId}/default-contact`,
      {
        method: 'POST',
        body: JSON.stringify({ email }),
        headers: { 'Content-Type': 'application/json' },
      },
    )
  },

  async initiate(args: InitiateArgs) {
    return request<InitiateResponse>('/api/orgs/connections/initiate', {
      method: 'POST',
      body: JSON.stringify({
        recipientEmail: args.recipientEmail,
        sourceFolderId: args.sourceFolderId ?? null,
        additionalFolderIds: args.additionalFolderIds ?? [],
        recipientCompanyTenantId: args.recipientCompanyTenantId ?? null,
        // Default lives at 'readcopy' (post-migration-0064 lattice; the
        // dropped 'read' tier maps to 'readcopy'). Server's initiate
        // validator allows only `readcopy | readwrite`; defaulting to
        // 'read' here used to silently 400 the connection-only flow.
        permission: args.permission ?? 'readcopy',
        note: args.note ?? null,
        setAsDefaultContact: args.setAsDefaultContact ?? false,
        organizationName: args.organizationName ?? null,
        allowSharing: args.allowSharing ?? null,
        recipientIsPersonal: args.recipientIsPersonal ?? false,
        // I4: a level change replaces the card's grant in one server step — never dropped here, or
        // the change silently becomes a plain re-share (which can raise, but never lower, access).
        ...(args.replacesAccess ? { replacesAccess: true } : {}),
      }),
      headers: { 'Content-Type': 'application/json' },
    })
  },

  // ── Recipient picker inbox + actions ────────────────────────────────

  async listIncoming() {
    return request<{ incoming: IncomingRequest[] }>('/api/orgs/connections/incoming')
  },

  /** Recipient picks where to accept: `pickTenantId` = an org the actor
   *  is a member of, OR null for "Personal" (no org connection created). */
  async recipientPick(requestId: string, pickTenantId: string | null) {
    return request<{
      ok: true
      state: 'pending_initiator_confirm' | 'established'
      canonicalSharedId: string | null
      /** True when the picked org was already connected — established by
       *  reuse, no confirm step. */
      alreadyConnected?: boolean
      /** #773: the org already had this shared folder (`all` of the share's folders, or
       *  `some`) and you can't open it yet — someone who manages it has been asked. */
      contactNoAccess?: 'all' | 'some'
    }>(`/api/orgs/connections/${requestId}/recipient-pick`, {
      method: 'POST',
      body: JSON.stringify({ pickTenantId }),
      headers: { 'Content-Type': 'application/json' },
    })
  },

  /** Recipient declines an incoming request (only while awaiting their
   *  pick). `block` also adds the initiator's tenant to this tenant's
   *  connection blocklist (reject-and-block). */
  async recipientReject(requestId: string, note?: string | null, block?: boolean) {
    return request<{ ok: true; state: 'closed_rejected'; alreadyConnected?: boolean }>(
      `/api/orgs/connections/${requestId}/recipient-reject`,
      {
        method: 'POST',
        body: JSON.stringify({ note: note ?? null, block: !!block }),
        headers: { 'Content-Type': 'application/json' },
      },
    )
  },

  // ── Initiator outbox + actions ──────────────────────────────────────

  /** `folderId` narrows the outbox to the requests sharing that folder (the
   *  Share dialog's Pending section, #1075). */
  async listOutgoing(as?: { tenantId?: string | null; orgEmail?: string | null; folderId?: string | null }) {
    const q = as?.folderId ? `?folderId=${encodeURIComponent(as.folderId)}` : ''
    return request<{ outgoing: OutgoingRequest[] }>(`/api/orgs/connections/outgoing${q}`, {
      headers: asHeaders(as),
    })
  },

  /** Edit a still-pending request's requested access (permission +
   *  allow-sharing) before it's accepted. Initiator / owner-admin only. */
  async updateRequestAccess(requestId: string, permission: string, allowSharing: boolean | null) {
    // `permission` = the level stored after the server's clamp (absent from an older API).
    return request<{ ok: true; permission?: string }>(`/api/orgs/connections/${requestId}/update-access`, {
      method: 'POST',
      body: JSON.stringify({ permission, allowSharing }),
      headers: { 'Content-Type': 'application/json' },
    })
  },

  /** Cross-tenant "awaiting your confirmation" inbox (avatar-menu
   *  cards). User-global; only returns requests the caller can confirm
   *  (owner/admin of the initiator tenant). */
  async listPendingConfirmations() {
    return request<{ pendingConfirmations: PendingConfirmation[] }>(
      '/api/orgs/connections/pending-confirmations',
    )
  },

  /** Initiator confirms after the recipient picked an org. `asTenantId`
   *  overrides the active-tenant header so the avatar menu can confirm a
   *  request belonging to ANY of the user's owner/admin tenants without
   *  switching context first.
   *
   *  `overrides` is set only by the Add-to-Drive approval dialog
   *  (share_link_mount): the confirming admin can dial the recipient's
   *  permission and re-sharing at approval time instead of accepting the
   *  link default. Permission is still capped server-side at the admin's
   *  own access. */
  async confirm(
    requestId: string,
    asTenantId?: string,
    overrides?: { permission?: 'readcopy' | 'readwrite'; allowSharing?: boolean },
    /** An owner/admin email of `asTenantId` — an identity override so a
     *  cross-tenant confirm (esp. a share_link_mount access override, gated on
     *  canManageConnection) resolves the right identity, not the active one. */
    asOrgEmail?: string | null,
  ) {
    return request<{
      ok: true
      state: 'established'
      canonicalSharedId: string | null
      /** The level the recipient org actually got — lower than picked when the request
       *  came from a link that gives the asker less (#848). */
      grantedPermission?: string | null
      existingShare?: boolean
    }>(`/api/orgs/connections/${requestId}/confirm`, {
      method: 'POST',
      body: JSON.stringify(overrides ?? {}),
      headers: {
        'Content-Type': 'application/json',
        ...asHeaders({ tenantId: asTenantId, orgEmail: asOrgEmail }),
      },
    })
  },

  /** Revoke (disconnect) an active connection. Owner/admin role on the
   *  caller's tenant required. Also cancels in-flight share_requests
   *  between the two tenants (server-side, in the same transaction). */
  async revoke(connectionId: string) {
    return request<{ ok: true }>(`/api/orgs/connections/${connectionId}`, {
      method: 'DELETE',
    })
  },

  /** Initiator rejects (cancels) their own request. `block` also adds the
   *  recipient tenant to this tenant's connection blocklist (decline-and-
   *  block; ignored when resending). */
  async reject(requestId: string, note?: string | null, resend?: boolean, block?: boolean) {
    return request<{ ok: true; state: 'closed_rejected' | 'pending_recipient' }>(
      `/api/orgs/connections/${requestId}/reject`,
      {
        method: 'POST',
        body: JSON.stringify({ note: note ?? null, resend: !!resend, block: !!block }),
        headers: { 'Content-Type': 'application/json' },
      },
    )
  },

  // ── Connection policy (require-admin-approval toggle) ───────────────

  /** This tenant's notify-admin policy. `canManage` ⇒ owner/admin of an org
   *  tenant (the toggle is editable). */
  async getSettings() {
    return request<{ notifyAdmin: boolean; canManage: boolean }>(
      '/api/orgs/connections/settings',
    )
  },

  /** Owner/admin: whether this tenant's admins are notified when a member
   *  forms a new connection (informational — does not gate the connection). */
  async setNotifyAdmin(notifyAdmin: boolean) {
    return request<{ ok: true; notifyAdmin: boolean }>(
      '/api/orgs/connections/settings',
      { method: 'POST', body: JSON.stringify({ notifyAdmin }), headers: { 'Content-Type': 'application/json' } },
    )
  },

  // ── Reshare referrals — the source owner approves an onward reshare ──

  /** Reshare referrals awaiting THIS org's (the source owner's) approval.
   *  Returns [] for non-owner/admin callers (server-enforced). Pass an identity
   *  override (`as`) to read as an owner/admin of ANOTHER tenant — used to
   *  approve a referral from a notification without switching the active tenant. */
  async listReshareReferrals(as?: { tenantId?: string | null; orgEmail?: string | null }) {
    return request<{ referrals: ReshareReferral[] }>('/api/orgs/connections/reshare-referrals', {
      headers: asHeaders(as),
    })
  },

  /** USER-GLOBAL: pending reshare referrals the caller can approve via ANY of
   *  their identities (owner/admin in the owner tenant), each tagged with the
   *  owner tenant. Used to tell the user which identity to switch to rather than
   *  showing a misleading "no longer active" when the ACTIVE identity can't
   *  approve (wrong tenant, or a non-admin email in the right tenant). */
  async listReshareReferralsPending() {
    return request<{ referrals: Array<{ requestId: string; ownerTenantId: string; ownerTenantName: string }> }>(
      '/api/orgs/connections/reshare-referrals/pending',
    )
  },

  async sourceApprove(
    requestId: string,
    access?: { permission?: string; allowSharing?: boolean },
    as?: { tenantId?: string | null; orgEmail?: string | null },
  ) {
    return request<{ ok: true; state: string; canonicalSharedId: string | null; grantedPermission?: string | null; existingShare?: boolean }>(
      `/api/orgs/connections/${requestId}/source-approve`,
      {
        method: 'POST',
        // The source owner can set the recipient's access at approval (only the
        // owner may grant read-write / enable onward sharing on a reshare).
        body: JSON.stringify({
          ...(access?.permission != null ? { permission: access.permission } : {}),
          ...(typeof access?.allowSharing === 'boolean' ? { allowSharing: access.allowSharing } : {}),
        }),
        // Identity override lets a notification click approve as an owner/admin
        // of the owner tenant without switching the active tenant.
        headers: { 'Content-Type': 'application/json', ...asHeaders(as) },
      },
    )
  },

  async sourceReject(requestId: string, note?: string | null, as?: { tenantId?: string | null; orgEmail?: string | null }) {
    return request<{ ok: true; state: string }>(
      `/api/orgs/connections/${requestId}/source-reject`,
      { method: 'POST', body: JSON.stringify({ note: note ?? null }), headers: { 'Content-Type': 'application/json', ...asHeaders(as) } },
    )
  },

  /** Resharer withdraws their pending reshare referral. */
  async cancelReshareReferral(requestId: string) {
    return request<{ ok: true; state: string }>(
      `/api/orgs/connections/${requestId}/referrer-cancel`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' } },
    )
  },

  /** Resharer edits the proposed access of a pending reshare referral
   *  (re-clamped server-side to the resharer's own ceiling). */
  async updateReshareReferralAccess(requestId: string, permission: 'readcopy' | 'readwrite') {
    return request<{ ok: true; permission: string }>(
      `/api/orgs/connections/${requestId}/referrer-access`,
      { method: 'PATCH', body: JSON.stringify({ permission }), headers: { 'Content-Type': 'application/json' } },
    )
  },

  // ── Connection blocklist (owner/admin) ──────────────────────────────

  /** This tenant's blocked organizations (reject-and-block / admin
   *  reject-and-block / manual block). Owner/admin only. */
  async listBlocks() {
    return request<{
      blocks: Array<{
        id: string
        blockedTenantId: string | null
        tenantName: string | null
        blockedEmail: string | null
        reason: string | null
        createdAt: string
      }>
    }>('/api/orgs/connections/blocklist')
  },

  /** Unblock (remove a block row owned by this tenant). Owner/admin only. */
  async removeBlock(id: string) {
    return request<{ ok: boolean }>(
      `/api/orgs/connections/blocklist/${id}`,
      { method: 'DELETE' },
    )
  },

  /** What a block would end (dw D6): blocking a CONNECTED organization disconnects the two and
   *  removes every folder shared between them, both ways. `requestId` — the sender a Decline &
   *  block would block; `tenantId` (+ `personal`: as the caller's personal drive, the invitation
   *  block) — a tenant to block. */
  async blockImpact(q: { requestId?: string; tenantId?: string; personal?: boolean }) {
    const params = new URLSearchParams()
    if (q.requestId) params.set('requestId', q.requestId)
    if (q.tenantId) params.set('tenantId', q.tenantId)
    if (q.personal) params.set('personal', '1')
    return request<BlockImpact>(`/api/orgs/connections/block-impact?${params}`)
  },
}

export interface BlockImpact {
  endsConnection: boolean
  sharedFolders: number
  tenantName: string | null
  /** A Decline & block the caller may not make: their org is connected to the sender, and ending
   *  that connection needs connection management. */
  needsManager?: boolean
  /** The block would be the caller's personal drive blocking an organization they belong to —
   *  never allowed (owner ruling 2026-10-19). */
  ownOrg?: boolean
  /** (`requestId` form) the request names this organization, and the caller acts as another — the
   *  block can only be made from it (switch to it, or just reject). */
  blockFromTenantName?: string
  /** (`requestId` form) who Reject & block would block — the request's live decline target. */
  declineTargetEmail?: string
}

/**
 * dw D6 (owner ruling 2026-10-19): the confirmation a block needs when it would END a connection —
 * null when it ends none (nothing to warn about). The ONE wording for every block entry point
 * (Decline & block, the invitation block).
 */
export function blockEndsConnectionConfirm(impact: BlockImpact | null): { title: string; message: string; confirmText: string } | null {
  // Couldn't find out (the check failed): ask anyway — never block-and-disconnect unwarned.
  if (!impact) {
    return {
      title: 'Block?',
      message: "We couldn't check whether you're connected. If you are, blocking also disconnects you and removes access to the folders shared between you (both ways). Block anyway?",
      confirmText: 'Block',
    }
  }
  if (!impact.endsConnection) return null
  const who = impact.tenantName ?? 'this organization'
  const folders = impact.sharedFolders === 0 ? ''
    : ` and remove access to ${impact.sharedFolders === 1 ? '1 shared folder' : `${impact.sharedFolders} shared folders`} (both ways)`
  return {
    title: `Block ${who}?`,
    message: `You're connected to ${who}. Blocking will also disconnect you${folders}. Block anyway?`,
    confirmText: 'Block and disconnect',
  }
}
